import { z } from 'zod';
import { type FieldDef, getField, OBJECT_NAMES, type ObjectDef, OBJECTS } from './objects';
import { filterSchema } from './validation';

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export const REPORT_FORMATS = ['tabular', 'summary', 'matrix'] as const;
export const DATE_GROUPINGS = ['day', 'month', 'quarter', 'year'] as const;
export const AGGREGATE_FNS = ['count', 'sum', 'avg', 'min', 'max'] as const;
export const CHART_TYPES = ['bar', 'column', 'line', 'donut', 'metric', 'table'] as const;
export type ChartType = (typeof CHART_TYPES)[number];

/** Field types a report can group by, and types it can sum or average. */
export const GROUPABLE_TYPES: ReadonlySet<FieldDef['type']> = new Set(['picklist', 'stage', 'user', 'reference', 'lookup', 'boolean', 'date', 'datetime', 'text']);
export const NUMERIC_TYPES: ReadonlySet<FieldDef['type']> = new Set(['number', 'currency', 'percent']);

export const REPORT_MAX_ROWS = 2000;

const groupingSchema = z.object({
  field: z.string().min(1).max(64),
  /** Required for date fields; ignored otherwise. */
  dateGrouping: z.enum(DATE_GROUPINGS).optional(),
});
export type ReportGrouping = z.infer<typeof groupingSchema>;

const aggregateSchema = z.object({ fn: z.enum(AGGREGATE_FNS), field: z.string().max(64).optional() });
export type ReportAggregate = z.infer<typeof aggregateSchema>;

/**
 * A report definition is data, never SQL. This schema checks its shape; definitionIssues then checks
 * every field name against the viewer's object definition (custom fields included, hidden fields not),
 * and the server compiles it with the viewer's access.
 */
export const reportDefinitionSchema = z
  .object({
    object: z.enum(OBJECT_NAMES),
    format: z.enum(REPORT_FORMATS),
    columns: z.array(z.string().min(1).max(64)).max(20).default([]),
    filters: z.array(filterSchema).max(20).default([]),
    scope: z.enum(['all', 'mine']).default('all'),
    rowGroupings: z.array(groupingSchema).max(2).default([]),
    columnGroupings: z.array(groupingSchema).max(1).default([]),
    aggregates: z.array(aggregateSchema).max(5).default([{ fn: 'count' }]),
    sort: z.object({ field: z.string().max(64), dir: z.enum(['asc', 'desc']) }).optional(),
    showDetails: z.boolean().default(true),
    chart: z.enum(CHART_TYPES).optional(),
  })
  .strict();
export type ReportDefinition = z.infer<typeof reportDefinitionSchema>;

/** Structural checks shared by the API and the builder UI. */
export function definitionIssues(d: ReportDefinition, obj: ObjectDef = OBJECTS[d.object]): { path: string; message: string }[] {
  const out: { path: string; message: string }[] = [];
  const field = (name: string) => getField(obj, name);
  for (const c of d.columns) {
    const f = field(c);
    if (!f) out.push({ path: 'columns', message: `Unknown column "${c}"` });
    else if (f.type === 'textarea') out.push({ path: 'columns', message: `${f.label} can't be shown in reports` });
  }
  const groups = [...d.rowGroupings, ...d.columnGroupings];
  for (const g of groups) {
    const f = field(g.field);
    if (!f || !GROUPABLE_TYPES.has(f.type)) out.push({ path: 'rowGroupings', message: `You can't group by "${g.field}"` });
    else if ((f.type === 'date' || f.type === 'datetime') && !g.dateGrouping) out.push({ path: 'rowGroupings', message: `Choose how to group ${f.label} (day, month, quarter or year)` });
  }
  if (new Set(groups.map((g) => g.field)).size !== groups.length) out.push({ path: 'rowGroupings', message: 'Group by each field only once' });
  if (d.format === 'tabular' && groups.length) out.push({ path: 'format', message: 'Tabular reports have no groupings' });
  if (d.format === 'summary' && (!d.rowGroupings.length || d.columnGroupings.length)) out.push({ path: 'rowGroupings', message: 'Summary reports group rows by one or two fields' });
  if (d.format === 'matrix' && (d.rowGroupings.length !== 1 || d.columnGroupings.length !== 1)) out.push({ path: 'columnGroupings', message: 'Matrix reports group by one row field and one column field' });
  if (!d.aggregates.length) out.push({ path: 'aggregates', message: 'Add at least one summary' });
  for (const a of d.aggregates) {
    if (a.fn === 'count') continue;
    const f = a.field ? field(a.field) : undefined;
    if (!f || !NUMERIC_TYPES.has(f.type)) out.push({ path: 'aggregates', message: `${a.fn} needs a number, currency or percent field` });
  }
  if (d.sort && !field(d.sort.field)) out.push({ path: 'sort', message: 'Unknown sort field' });
  return out;
}

const FN_LABEL: Record<(typeof AGGREGATE_FNS)[number], string> = { count: 'Record count', sum: 'Sum of', avg: 'Average', min: 'Smallest', max: 'Largest' };

/** "Sum of Amount", "Record count", … */
export function aggregateLabel(obj: ObjectDef, a: ReportAggregate): string {
  if (a.fn === 'count') return FN_LABEL.count;
  return `${FN_LABEL[a.fn]} ${a.field ? (getField(obj, a.field)?.label ?? a.field) : ''}`.trim();
}

/** Default columns for a new report: the object's list-view columns. */
export const defaultColumns = (obj: ObjectDef) => obj.fields.filter((f) => f.listDefault).map((f) => f.name);

export const reportSaveSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(1000).nullable().optional(),
    folderId: z.string().uuid().nullable().default(null),
    definition: reportDefinitionSchema,
  })
  .strict();

export interface ReportColumn { name: string; label: string; type: FieldDef['type'] }

/**
 * One aggregated cell. `keys` has one entry per grouping (row groupings, then column groupings).
 * `rolledUp[i]` is true when grouping i is summed over (a subtotal); all true = grand total.
 */
export interface ReportGroupRow {
  keys: (string | null)[];
  rolledUp: boolean[];
  count: number;
  values: (number | null)[];
}

/** The grand-total row of a result. */
export const grandTotal = (r: Pick<ReportResult, 'groups'>) => r.groups.find((g) => g.rolledUp.every(Boolean));

export interface ReportResult {
  definition: ReportDefinition;
  columns: ReportColumn[];
  /** Detail rows (tabular, and summary when showDetails), ordered by groupings first. */
  rows: Record<string, unknown>[];
  /** Aggregates for every grouping level, including the grand total (level = all bits set). */
  groups: ReportGroupRow[];
  /** Display names for user/stage/reference ids and picklist values, keyed by raw value. */
  labels: Record<string, string>;
  total: number;
  truncated: boolean;
  aggregateLabels: string[];
}

export interface ReportSummary {
  id: string;
  name: string;
  description: string | null;
  folderId: string | null;
  folderName: string | null;
  ownerId: string;
  ownerName: string;
  object: string;
  format: string;
  updatedAt: string;
  canEdit: boolean;
}

// ---------------------------------------------------------------------------
// Folders (shared by reports and dashboards)
// ---------------------------------------------------------------------------

export const FOLDER_ACCESS = ['view', 'edit', 'manage'] as const;
export type FolderAccess = (typeof FOLDER_ACCESS)[number];

export const folderSchema = z.object({ name: z.string().trim().min(1).max(120) }).strict();
export const folderShareSchema = z
  .object({
    granteeType: z.enum(['user', 'role', 'role_subordinates', 'group']),
    granteeId: z.string().uuid(),
    access: z.enum(FOLDER_ACCESS),
  })
  .strict();

export interface Folder {
  id: string;
  name: string;
  access: FolderAccess;
  createdByName: string;
  shares?: { granteeType: string; granteeId: string; granteeName: string; access: FolderAccess }[];
}

// ---------------------------------------------------------------------------
// Dashboards
// ---------------------------------------------------------------------------

export const dashboardComponentSchema = z
  .object({
    id: z.string().min(1).max(40),
    reportId: z.string().uuid(),
    title: z.string().trim().max(120).optional(),
    chart: z.enum(CHART_TYPES),
    /** Which summary of the report to plot. */
    aggregateIndex: z.number().int().min(0).max(4).default(0),
    width: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(1),
  })
  .strict();
export type DashboardComponent = z.infer<typeof dashboardComponentSchema>;

export const dashboardSaveSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(1000).nullable().optional(),
    folderId: z.string().uuid().nullable().default(null),
    components: z.array(dashboardComponentSchema).max(20),
  })
  .strict();

export interface DashboardComponentData {
  id: string;
  /** Null when the viewer can't open the source report. */
  result: ReportResult | null;
  error?: string;
  reportName?: string;
}

// ---------------------------------------------------------------------------
// Forecasts
// ---------------------------------------------------------------------------

export const PERIOD_TYPES = ['month', 'quarter'] as const;
export type PeriodType = (typeof PERIOD_TYPES)[number];

/** First day (YYYY-MM-DD) of the month or quarter containing `date`. */
export function periodStart(type: PeriodType, date: string | Date): string {
  const d = typeof date === 'string' ? new Date(`${date.slice(0, 10)}T00:00:00Z`) : date;
  const m = type === 'quarter' ? Math.floor(d.getUTCMonth() / 3) * 3 : d.getUTCMonth();
  return new Date(Date.UTC(d.getUTCFullYear(), m, 1)).toISOString().slice(0, 10);
}

/** Exclusive end (YYYY-MM-DD) of the period starting at `start`. */
export function periodEnd(type: PeriodType, start: string): string {
  const d = new Date(`${start}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + (type === 'quarter' ? 3 : 1));
  return d.toISOString().slice(0, 10);
}

export function periodLabel(type: PeriodType, start: string): string {
  const d = new Date(`${start}T00:00:00Z`);
  if (type === 'quarter') return `Q${Math.floor(d.getUTCMonth() / 3) + 1} ${d.getUTCFullYear()}`;
  return d.toLocaleString('en', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export const periodQuerySchema = z.object({
  periodType: z.enum(PERIOD_TYPES).default('quarter'),
  periodStart: z.string().regex(/^\d{4}-\d{2}-01$/).optional(),
  roleId: z.string().uuid().optional(),
});

/**
 * Cumulative forecast rollups, as in collaborative forecasting:
 *  closed = Closed; commit = Closed + Commit; bestCase = Closed + Commit + Best Case;
 *  openPipeline = Pipeline + Best Case + Commit (open opportunities only). Omitted is excluded.
 */
export interface ForecastAmounts { closed: number; commit: number; bestCase: number; openPipeline: number }
export const FORECAST_ROLLUPS = ['closed', 'commit', 'bestCase', 'openPipeline'] as const;
export type ForecastRollup = (typeof FORECAST_ROLLUPS)[number];

export function cumulative(byCategory: Partial<Record<'pipeline' | 'best_case' | 'commit' | 'closed', number>>): ForecastAmounts {
  const v = (k: 'pipeline' | 'best_case' | 'commit' | 'closed') => byCategory[k] ?? 0;
  return {
    closed: v('closed'),
    commit: v('closed') + v('commit'),
    bestCase: v('closed') + v('commit') + v('best_case'),
    openPipeline: v('pipeline') + v('best_case') + v('commit'),
  };
}

export interface ForecastRow {
  /** 'user' = one person's own opportunities; 'role' = everyone in a direct sub-role and below; 'total' = the view's total. */
  kind: 'user' | 'role' | 'total';
  id: string;
  name: string;
  quota: number | null;
  amounts: ForecastAmounts;
  /** Manager adjustments made by the viewer on this row (commit / best case). */
  adjusted: Partial<Pick<ForecastAmounts, 'commit' | 'bestCase'>>;
}

export interface ForecastView {
  periodType: PeriodType;
  periodStart: string;
  periodEnd: string;
  role: { id: string; name: string } | null;
  rows: ForecastRow[];
  total: ForecastRow;
  canAdjust: boolean;
  /** Roles the viewer may switch to (their own and below; all for View All on opportunities). */
  roles: { id: string; name: string; depth: number }[];
}

export const quotaSchema = z
  .object({ userId: z.string().uuid(), periodType: z.enum(PERIOD_TYPES), periodStart: z.string().regex(/^\d{4}-\d{2}-01$/), amount: z.number().min(0).max(1e13) })
  .strict();

export const adjustmentSchema = z
  .object({
    subjectType: z.enum(['user', 'role']),
    subjectId: z.string().uuid(),
    periodType: z.enum(PERIOD_TYPES),
    periodStart: z.string().regex(/^\d{4}-\d{2}-01$/),
    rollup: z.enum(['commit', 'bestCase']),
    /** null removes the adjustment. */
    amount: z.number().min(0).max(1e13).nullable(),
    note: z.string().trim().max(500).nullable().optional(),
  })
  .strict();

export interface TrendPoint { date: string; pipeline: number; bestCase: number; commit: number; closed: number }

export interface QuotaRow { userId: string; periodType: string; periodStart: string; amount: number }

/**
 * One salesperson's target for one month, the same everywhere (Home, KPIs, CEO, AI): when any month of that quarter
 * has a monthly quota, the monthly quotas count (a month without one is 0); else the quarter's quota, a third a month.
 * Null when neither is set. So a quarter quota entered on top of monthly ones is never counted twice.
 */
export function monthQuota(rows: QuotaRow[], userId: string, month: string): number | null {
  const q = periodStart('quarter', month);
  const monthly = rows.filter((r) => r.userId === userId && r.periodType === 'month' && periodStart('quarter', r.periodStart) === q);
  if (monthly.length) return monthly.find((r) => r.periodStart === month.slice(0, 10))?.amount ?? 0;
  const quarter = rows.find((r) => r.userId === userId && r.periodType === 'quarter' && r.periodStart === q);
  return quarter ? quarter.amount / 3 : null;
}

/** The target for the months starting from `from` up to (not including) `to`, for these people (all with a quota when null). */
export function rangeQuota(rows: QuotaRow[], userIds: Iterable<string> | null, from: string, to: string): number | null {
  const users = userIds ? [...userIds] : [...new Set(rows.map((r) => r.userId))];
  let total: number | null = null;
  for (let m = periodStart('month', from); m < to; m = periodEnd('month', m)) {
    for (const u of users) {
      const v = monthQuota(rows, u, m);
      if (v != null) total = (total ?? 0) + v;
    }
  }
  // To the halala: a quarter quota split in thirds adds back to exactly the quarter's amount.
  return total == null ? null : Math.round(total * 100) / 100;
}

/**
 * Plan next year: the suggested target is this year's wins grown by `growthPct`, never below the likely pipeline already
 * expected next year, rounded to SAR 10,000.
 */
export function suggestTarget(won: number, pipelineLikely: number, growthPct: number): number {
  return Math.round(Math.max(won * (1 + growthPct / 100), pipelineLikely) / 10_000) * 10_000;
}

/** A yearly target split into quarters by `shares` (adding up to 1), rounded to SAR 1,000; the last quarter takes the rounding. */
export function splitByShares(total: number, shares: readonly number[]): [number, number, number, number] {
  const q = shares.slice(0, 3).map((s) => Math.round((total * s) / 1000) * 1000);
  return [q[0]!, q[1]!, q[2]!, Math.max(0, total - q[0]! - q[1]! - q[2]!)];
}
